import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = [
        'tab', 'typePanel',
        'strip', 'chip', 'stripRange', 'stripPrev', 'stripNext',
        'calendar', 'calendarToggle', 'calendarMonth',
        'mobileCourt', 'mobileSlots',
        'cellDate', 'cellTime', 'cellDurationRow', 'cellDuration',
        'cellType', 'cellCourt', 'cellError', 'cellAdd', 'cellRemove',
        'cart', 'cartTotal', 'cartNote', 'cartEmpty', 'cartClear', 'cartTimer', 'checkout',
        'sheet', 'sheetToggle',
    ];
    static values = {
        durationsUrl: String,
        addUrl: String,
        cancelUrl: String,
        cancelAllUrl: String,
        redirect: String,
        date: String,
        total: Number,
        plural: Array,
        openCart: Boolean,
        nonce: String,
    };

    connect() {
        // a sport's own URL redirects here with #slug - open that tab and keep it like a manual pick
        const hashTab = this.tabTargets.find((tab) => window.location.hash === `#${tab.dataset.typeSlug}`);

        if (hashTab) {
            sessionStorage.setItem('cr-type', hashTab.dataset.typeId);
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        // keep the court type and the phone court picked before the reload
        const typeID = sessionStorage.getItem('cr-type');

        if (typeID && this.tabTargets.some((tab) => tab.dataset.typeId === typeID)) {
            this.showType(typeID);
        }

        const courtID = sessionStorage.getItem('cr-court');
        const court = this.mobileCourtTargets.find((button) => button.dataset.courtId === courtID);

        if (court) {
            this.showCourt(court);
        }

        // app: lift the fixed cart bar above the app's bottom navigation
        const appFooter = document.querySelector('.sticky-footer');

        if (appFooter) {
            this.element.style.setProperty('--cr-bar-offset', `${appFooter.offsetHeight}px`);
        }

        // the theme stylesheet loads async - measure and scroll only once the layout is final
        if (document.readyState === 'complete') {
            this.restoreView();
        } else {
            this.onLoad = () => this.restoreView();
            window.addEventListener('load', this.onLoad, { once: true });
        }
    }

    restoreView() {
        // scroll position from before a cell-triggered or date-change reload
        const scrollY = sessionStorage.getItem('cr-scroll');

        if (scrollY !== null) {
            sessionStorage.removeItem('cr-scroll');
            window.scrollTo({ top: parseInt(scrollY, 10), behavior: 'instant' });
        }

        this.setupStrip();

        // after login/register the user was mid-checkout - bring the cart back up
        if (this.openCartValue) {
            const url = new URL(window.location.href);
            url.searchParams.delete('open_cart');
            window.history.replaceState(null, '', url);

            if (this.hasSheetTarget && this.sheetTarget.offsetParent !== null) {
                this.toggleSheet();
            } else if (this.hasCartTarget) {
                this.cartTarget.scrollIntoView({ block: 'center' });
            }
        }
    }

    disconnect() {
        if (this.onLoad) {
            window.removeEventListener('load', this.onLoad);
        }

        this.stripObserver?.disconnect();
    }

    /**
     * Type tab switches the grid, phone view and price list; date stays put.
     */
    switchTab(event) {
        const typeID = String(event.currentTarget.dataset.typeId);

        sessionStorage.setItem('cr-type', typeID);
        this.showType(typeID);
    }

    showType(typeID) {
        for (const tab of this.tabTargets) {
            const active = tab.dataset.typeId === typeID;

            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-pressed', active ? 'true' : 'false');
        }

        for (const panel of this.typePanelTargets) {
            panel.classList.toggle('d-none', panel.dataset.typeId !== typeID);
        }
    }

    /**
     * Phone view: court chip shows that court's slot list.
     */
    pickCourt(event) {
        sessionStorage.setItem('cr-court', event.currentTarget.dataset.courtId);
        this.showCourt(event.currentTarget);
    }

    showCourt(button) {
        const panel = button.closest('.cr-mview');
        const courtID = button.dataset.courtId;

        for (const court of this.mobileCourtTargets.filter((el) => panel.contains(el))) {
            const active = court === button;

            court.classList.toggle('is-active', active);
            court.setAttribute('aria-pressed', active ? 'true' : 'false');
        }

        for (const slots of this.mobileSlotsTargets.filter((el) => panel.contains(el))) {
            slots.classList.toggle('d-none', slots.dataset.courtId !== courtID);
        }
    }

    /**
     * Day strip: 14 days per view on desktop, arrows page by a week.
     * Keeps the strip where it was when the picked day stays visible.
     */
    setupStrip() {
        if (!this.hasStripTarget || this.chipTargets.length < 2) {
            return;
        }

        this.stripSaved = parseInt(sessionStorage.getItem('cr-strip') ?? '', 10);
        this.stripPinned = false;

        sessionStorage.removeItem('cr-strip');

        // the layout keeps settling while async styles/fonts apply (and the viewport can change):
        // re-align on every strip resize, recomputing the start until the user moves the strip
        this.stripObserver = new ResizeObserver(() => this.alignStrip());
        this.stripObserver.observe(this.stripTarget);
    }

    alignStrip() {
        if (!this.stripReady()) {
            return;
        }

        if (!this.stripPinned) {
            const active = Math.max(0, this.chipTargets.findIndex((chip) => chip.classList.contains('is-active')));
            const visible = this.stripVisibleCount();
            const saved = this.stripSaved;

            this.stripIndex = active < visible ? 0 : Math.max(0, active - 3);

            if (!Number.isNaN(saved) && active >= saved && active < saved + visible) {
                this.stripIndex = saved;
            }
        }

        this.stripTarget.scrollLeft = this.chipTargets[this.stripIndex].offsetLeft - this.chipTargets[0].offsetLeft;
        this.updateStrip();
    }

    /**
     * User moved the strip (arrows, wheel, swipe) - from now on follow its scroll position.
     */
    pinStrip() {
        this.stripPinned = true;
    }

    stripScrolled() {
        if (this.stripPinned) {
            this.stripIndex = this.stripFirstVisible();
        }

        this.updateStrip();
    }

    stripReady() {
        return getComputedStyle(this.stripTarget).display === 'grid' && this.stripStep() > 0;
    }

    stripStep() {
        const chips = this.chipTargets;

        // averaged over the whole strip - single offsets are rounded to whole pixels
        return (chips[chips.length - 1].offsetLeft - chips[0].offsetLeft) / (chips.length - 1);
    }

    stripVisibleCount() {
        return Math.max(1, Math.round(this.stripTarget.clientWidth / this.stripStep()));
    }

    stripFirstVisible() {
        return Math.round(this.stripTarget.scrollLeft / this.stripStep());
    }

    stripPrev() {
        this.pinStrip();
        this.stripTarget.scrollBy({ left: -7 * this.stripStep(), behavior: 'smooth' });
    }

    stripNext() {
        this.pinStrip();
        this.stripTarget.scrollBy({ left: 7 * this.stripStep(), behavior: 'smooth' });
    }

    updateStrip() {
        if (!this.stripReady()) {
            return;
        }

        const chips = this.chipTargets;
        const first = Math.min(this.stripFirstVisible(), chips.length - 1);
        const last = Math.min(first + this.stripVisibleCount() - 1, chips.length - 1);
        const strip = this.stripTarget;

        if (this.hasStripRangeTarget) {
            this.stripRangeTarget.textContent = `${chips[first].dataset.label} – ${chips[last].dataset.label}`;
        }

        if (this.hasStripPrevTarget) {
            this.stripPrevTarget.disabled = strip.scrollLeft <= 1;
        }

        if (this.hasStripNextTarget) {
            this.stripNextTarget.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
        }
    }

    /**
     * Day link (strip or calendar) reloads the page with the new date.
     */
    pickDate() {
        this.rememberView();
    }

    /**
     * Remember page scroll and strip position for the upcoming reload.
     */
    rememberView() {
        if (this.stripIndex !== undefined) {
            sessionStorage.setItem('cr-strip', String(this.stripIndex));
        }

        sessionStorage.setItem('cr-scroll', String(window.scrollY));
    }

    toggleCalendar() {
        const open = this.calendarTarget.classList.toggle('d-none') === false;

        this.calendarToggleTarget.classList.toggle('is-active', open);
        this.calendarToggleTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    calendarPrev() {
        this.shiftCalendar(-1);
    }

    calendarNext() {
        this.shiftCalendar(1);
    }

    shiftCalendar(step) {
        const months = this.calendarMonthTargets;
        const current = months.findIndex((month) => !month.classList.contains('d-none'));
        const next = Math.min(Math.max(current + step, 0), months.length - 1);

        months.forEach((month, index) => month.classList.toggle('d-none', index !== next));
    }

    /**
     * Cell click opens the confirmation modal: add mode for free slots
     * (with duration select), remove mode for already selected slots.
     */
    pickCell(event) {
        const cell = event.currentTarget;
        const reservationID = cell.dataset.reservationId || null;

        this.pendingCell = {
            typeId: cell.dataset.typeId || null,
            courtId: cell.dataset.courtId || null,
            reservationId: reservationID,
            time: cell.dataset.time,
        };

        sessionStorage.setItem('cr-court', cell.dataset.courtId);

        this.cellTimeTarget.textContent = cell.dataset.time;
        this.cellTypeTarget.textContent = cell.dataset.type;
        this.cellCourtTarget.textContent = cell.dataset.courtName;
        this.cellErrorTarget.innerHTML = '';

        if (reservationID) {
            this.cellDurationRowTarget.classList.add('d-none');
            this.cellAddTarget.classList.add('d-none');
            this.cellRemoveTarget.classList.remove('d-none');
        } else {
            this.cellDurationRowTarget.classList.remove('d-none');
            this.cellRemoveTarget.classList.add('d-none');
            this.cellAddTarget.classList.remove('d-none');
            this.cellAddTarget.disabled = true;
            this.cellDurationTarget.innerHTML = '<option>Kraunama…</option>';
            this.cellDurationTarget.disabled = true;
            this.loadCellDurations(cell.dataset.courtId, cell.dataset.time);
        }

        document.dispatchEvent(new CustomEvent('modal:open', { detail: { id: 'court-cell' } }));
    }

    async loadCellDurations(courtID, time) {
        const url = `${this.durationsUrlValue}?court_id=${courtID}&date=${this.dateValue}&time=${encodeURIComponent(time)}`;

        let durations = [];

        try {
            const response = await fetch(url, { credentials: 'same-origin', headers: this.restHeaders() });
            const json = await response.json();
            durations = json.data?.durations || [];
        } catch (error) {
            durations = [];
        }

        // a stale response can arrive after another cell was opened
        if (!this.pendingCell || this.pendingCell.courtId !== courtID || this.pendingCell.time !== time) {
            return;
        }

        this.cellDurationTarget.innerHTML = '';

        for (const duration of durations) {
            const option = document.createElement('option');
            option.value = duration.duration_min;
            option.textContent = `${duration.duration_min} min – ${this.formatPrice(duration.price)}`;
            this.cellDurationTarget.appendChild(option);
        }

        if (durations.length > 0) {
            this.cellDurationRowTarget.classList.remove('d-none');
            this.cellDurationTarget.disabled = false;
            this.cellAddTarget.disabled = false;
        } else {
            // no bookable durations (e.g. end of working hours) - say so instead of showing an empty select
            this.cellDurationRowTarget.classList.add('d-none');
            this.cellErrorTarget.innerHTML = '<span class="general-error">Pasirinktam laikui nėra laisvų trukmių. Pasirinkite kitą laiką.</span>';
        }
    }

    async addFromModal(event) {
        if (!this.pendingCell || !this.cellDurationTarget.value) {
            return;
        }

        const button = event.currentTarget;
        this.setButtonLoading(button, true);

        await this.submitCell(this.addUrlValue, {
            type_id: this.pendingCell.typeId,
            court_id: this.pendingCell.courtId,
            date: this.dateValue,
            time: this.pendingCell.time,
            duration: this.cellDurationTarget.value,
            redirect: this.redirectValue,
        });

        // only reached when the submit failed (success reloads the page)
        this.setButtonLoading(button, false);
    }

    async removeFromModal(event) {
        if (!this.pendingCell) {
            return;
        }

        const button = event.currentTarget;
        this.setButtonLoading(button, true);

        await this.submitCell(this.cancelUrlValue, {
            reservation_id: this.pendingCell.reservationId,
        });

        this.setButtonLoading(button, false);
    }

    /**
     * Cart line removal without a page reload: deletes the cart items, frees
     * the grid and phone slots, drops the line from the sidebar and the sheet.
     */
    async removeFromCart(event) {
        const button = event.currentTarget;
        const ids = (button.dataset.ids || '').split(',').filter(Boolean);
        const price = parseFloat(button.dataset.price || '0');

        if (!ids.length) {
            return;
        }

        this.setButtonLoading(button, true);

        const { success, data } = await this.post(this.cancelUrlValue, { reservation_id: ids.join(',') });

        if (!success || data.general_error) {
            this.setButtonLoading(button, false);
            alert(data.general_error || data.message || 'Įvyko klaida. Bandykite dar kartą.');

            return;
        }

        for (const id of ids) {
            for (const cell of this.element.querySelectorAll(`[data-reservation-id="${id}"]`)) {
                this.freeCell(cell);
            }
        }

        for (const line of this.element.querySelectorAll(`.cr-line[data-cart-ids="${button.dataset.ids}"]`)) {
            line.remove();
        }

        this.totalValue = Math.max(0, this.totalValue - price);
        this.renderCart();
    }

    async clearCart(event) {
        const button = event.currentTarget;
        button.disabled = true;

        const { success, data } = await this.post(this.cancelAllUrlValue, {});

        if (!success || data.general_error) {
            button.disabled = false;
            alert(data.general_error || data.message || 'Įvyko klaida. Bandykite dar kartą.');

            return;
        }

        this.rememberView();
        window.location.reload();
    }

    toggleSheet() {
        const open = this.sheetTarget.classList.toggle('d-none') === false;

        this.element.classList.toggle('is-sheet-open', open);

        if (this.hasSheetToggleTarget) {
            this.sheetToggleTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
    }

    freeCell(cell) {
        const base = cell.classList.contains('cr-mslot') ? 'cr-mslot' : 'cr-cell';
        const sub = cell.querySelector('.cr-mslot__sub');

        cell.classList.replace(`${base}--selected`, `${base}--free`);
        cell.removeAttribute('data-reservation-id');

        if (sub && cell.dataset.freeLabel) {
            sub.textContent = cell.dataset.freeLabel;
        }
    }

    /**
     * Sync totals, notes, empty states and checkout buttons of the sidebar cart and the bar.
     */
    renderCart() {
        const lines = this.element.querySelectorAll('.cr-line');
        const count = new Set([...lines].map((line) => line.dataset.cartIds)).size;
        const empty = count === 0;

        for (const total of this.cartTotalTargets) {
            total.textContent = this.formatPrice(this.totalValue);
        }

        for (const note of this.cartNoteTargets) {
            note.textContent = empty ? note.dataset.empty : `${note.dataset.prefix || ''}${this.countLabel(count)}`;
        }

        for (const el of this.cartEmptyTargets) {
            el.classList.toggle('d-none', !empty);
        }

        for (const el of [...this.cartClearTargets, ...this.cartTimerTargets]) {
            el.classList.toggle('d-none', empty);
        }

        for (const button of this.checkoutTargets) {
            button.classList.toggle('is-disabled', empty);
        }
    }

    countLabel(count) {
        const forms = this.pluralValue;
        let form = 2;

        if (count % 10 === 1 && count % 100 !== 11) {
            form = 0;
        } else if (count % 10 >= 2 && (count % 100 < 10 || count % 100 >= 20)) {
            form = 1;
        }

        return `${count} ${forms[form] || ''}`.trim();
    }

    formatPrice(value) {
        return `${Number(value).toFixed(2).replace('.', ',')} €`;
    }

    setButtonLoading(button, loading) {
        button.classList.toggle('is-loading', loading);
        button.disabled = loading;
    }

    // logged-in WP users (editors testing private sports) need the REST nonce, or REST treats them as guests
    restHeaders() {
        return this.nonceValue ? { 'X-WP-Nonce': this.nonceValue } : {};
    }

    async post(url, fields) {
        const body = new FormData();

        for (const [key, value] of Object.entries(fields)) {
            body.append(key, value);
        }

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: this.restHeaders(),
                body,
            });
            const json = await response.json();

            return { success: !!json.success, data: json.data || {} };
        } catch (error) {
            return { success: false, data: {} };
        }
    }

    async submitCell(url, fields) {
        this.cellErrorTarget.innerHTML = '';

        const { success, data } = await this.post(url, fields);

        if (!success || data.general_error) {
            const message = data.general_error || data.message || 'Įvyko klaida. Bandykite dar kartą.';
            this.cellErrorTarget.innerHTML = `<span class="general-error">${message}</span>`;

            return false;
        }

        this.rememberView();
        window.location.reload();

        return true;
    }
}
